"use client";

/**
 * Puts the player on whichever page has audio, from one place in the root layout.
 *
 * WHY THIS EXISTS. The player was wired into five page templates by hand, which was fine for five
 * and absurd for ninety-four — and every new page would have had to remember to add it. Mounted in
 * the layout and keyed on the pathname, a page gets a player the moment its audio exists and never
 * needs to know about it. Nothing to wire, and nothing to forget.
 *
 * The index is fetched once per session, not once per navigation: it is the same file on every
 * route, and re-fetching it on each client-side move would be a request that can only ever return
 * what is already in memory. A page with no entry renders nothing at all.
 */

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import dynamic from "next/dynamic";

/* Loaded on demand, not with the layout. Importing the player directly put it in the ROOT layout's
   client bundle, which every page shares — and that reshuffled the shared chunk graph so pages that
   never needed the heavy vendor chunk began pulling it in anyway. A player most readers never open
   has no business in the critical path of every page on the site.

   ssr:false because there is nothing to render on the server: the mount only knows whether a page
   has audio after it has fetched the index. */
const PageAudio = dynamic(
  () => import("@/components/marketing/page-audio").then((m) => m.PageAudio),
  { ssr: false },
);

type NextRef = { href: string; name: string };
type Span = { name: string; start: number; end: number };
type Entry = {
  hash: string; title: string; duration: number;
  voice: string; asideVoice?: string; discussVoice?: string;
  next?: NextRef[]; spans?: Span[];
};

/* Module-level, so a reader moving between pages fetches this once. A failed fetch resolves to an
   empty index rather than rejecting — a missing spoken version is not an error, it is just a page
   without a player, which is the normal state of most of the site. */
let indexPromise: Promise<Record<string, Entry>> | null = null;
const loadIndex = () => {
  /* Revalidated, not just fetched. This file's whole job is to say what the current filenames are,
     and a copy from before the last rebuild names files that have since been swept. One small
     request per session, answered with a 304 when nothing has moved. */
  indexPromise ??= fetch("/shared/tts/askfinz.com/index.json", { cache: "no-cache" })
    .then((r) => (r.ok ? r.json() : {}))
    .catch(() => ({}));
  return indexPromise;
};

/* ROUTES WHOSE AUDIO IS BEHIND SOMETHING.
   The pitch deck has a spoken version, but it belongs to the deck: until the reader
   has been through the gate AND chosen which version they want, a narrator is
   either reading a form aloud or reading the wrong deck.

   NOT A STORAGE CHECK, AND THAT MATTERS. This first keyed off the deck session, so
   the player mounted the moment the gate passed — with the version chooser still on
   screen. That is not merely early: PageAudio AUTOPLAYS on mount for a reader who
   was already listening elsewhere on the site, so the pitch began narrating over
   the dialogue asking which pitch they wanted. Reading the session back after a
   reload had the same fault.

   So these routes start locked on every page load and are unlocked by one thing:
   the deck saying it is being read, which it says once a version is picked.

   THE EVENT ALSO NAMES WHICH NARRATION. The deck serves two versions from one URL,
   and there is a separate recording for each — 50 slides against 16, twenty-two
   minutes against seven. Keyed on the pathname alone this mount always loaded the
   long one, so a reader on the short deck heard the long narration while the
   slides advanced on the short deck's timings: every slide wrong, from the first
   one. The deck says which recording it wants, and says it again when the reader
   switches version. */
const GATED = new Set(["/scale/pitch_deck"]);
export const DECK_OPENED_EVENT = "askfinz:deck-opened";

export function PageAudioMount() {
  const pathname = usePathname();
  const [entry, setEntry] = useState<Entry | null>(null);
  /* The whole index, kept so a recommended page can be shown under its OWN title rather than a name
     derived from its URL. Only pages that have audio are in it; the rest fall back to the name the
     closing line spoke, which is why the drafter records both. */
  const [index, setIndex] = useState<Record<string, Entry>>({});

  /* Undefined until the effect below has looked, so the first paint never guesses:
     rendering a player and then pulling it away is worse than waiting a frame. */
  const [unlocked, setUnlocked] = useState<boolean | null>(null);
  /* Set by a gated route to name its own narration, since its URL cannot. */
  const [override, setOverride] = useState<string | null>(null);
  useEffect(() => {
    if (!GATED.has(pathname.replace(/\/+$/, "") || "/")) { setUnlocked(true); return; }
    setUnlocked(false);
    setOverride(null);
    const open = (e: Event) => {
      const route = (e as CustomEvent<{ route?: string }>).detail?.route;
      if (route) setOverride(route);
      setUnlocked(true);
    };
    window.addEventListener(DECK_OPENED_EVENT, open);
    return () => window.removeEventListener(DECK_OPENED_EVENT, open);
  }, [pathname]);

  useEffect(() => {
    let live = true;
    /* Clear first: without this, moving from a page that has audio to one that does not would
       leave the previous page's player on screen until the lookup resolved. */
    setEntry(null);
    loadIndex().then((all) => {
      if (!live) return;
      const key = override ?? (pathname.replace(/\/+$/, "") || "/");
      setIndex(all);
      setEntry(all[key] ?? all[pathname] ?? null);
    });
    return () => { live = false; };
  }, [pathname, override]);

  if (!entry || unlocked !== true) return null;
  /* Keyed on the hash so every page gets a fresh player. Without it React can reuse the instance
     when one route's lookup resolves straight into the next, and the previous page's sentence
     timings are kept — loadMarks returns early once marks are set, so the bar would caption this
     page's audio with the last page's sentences. */
  /* A card is a label, not a sentence. Page titles are display headings and several end in a full
     stop — "Research." — which reads as a typo beside an arrow. The spoken name is the fallback and
     needs the same treatment plus a capital, since it is written to be said mid-sentence. */
  const label = (t: string) => t.trim().replace(/\s*[.]+$/, "");
  const next = (entry.next ?? []).map((n) => {
    /* The page's own title where it is a NAME, the spoken name where it is a headline. Several
       titles are editorial sentences — "We don't rent our search results. We read the web
       ourselves." — which truncate to nothing useful in a card and are not what the listener just
       heard anyway. Short titles ("Research", "What is a web crawler?") are better than a name
       derived from a URL, so they win when they fit. */
    const own = index[n.href]?.title ? label(index[n.href].title) : "";
    const spoken = label(n.name).replace(/^(?!askFinz)(.)/, (c) => c.toUpperCase());
    return { ...n, name: spoken, title: own && own.length <= 42 ? own : undefined };
  });

  return (
    <PageAudio
      key={entry.hash}
      hash={entry.hash}
      title={entry.title}
      duration={entry.duration}
      asideVoice={entry.asideVoice}
      discussVoice={entry.discussVoice}
      spans={entry.spans}
      next={next}
    />
  );
}
